import { messaging, getToken, VAPID_KEY } from '../config/firebase';
import { API_URL, USER_ROLES } from '../constant/constant';

/**
 * Request notification permission from the user
 * @returns {Promise<boolean>} Whether permission was granted
 */
export async function requestNotificationPermission() {
  try {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return false;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      console.log('Notification permission granted');
      return true;
    } else {
      console.log('Notification permission denied');
      return false;
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
}

/**
 * Get FCM token from Firebase
 * @returns {Promise<string|null>} FCM token or null if failed
 */
export async function getFCMToken() {
  try {
    if (!messaging) {
      console.warn('Firebase Messaging not initialized');
      return null;
    }

    // Request permission first
    const hasPermission = await requestNotificationPermission();
    if (!hasPermission) {
      return null;
    }

    // Get the FCM token
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
    });

    if (token) {
      console.log('FCM Token obtained:', token.substring(0, 20) + '...');
      return token;
    } else {
      console.log('No FCM token available. Request permission to generate one.');
      return null;
    }
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
}

/**
 * Register FCM token with backend
 * @param {string} sessionToken - User session token
 * @param {string} userRole - User role (Vendor or User)
 * @param {string} ownerId - User ID or Vendor ID
 * @returns {Promise<boolean>} Whether registration was successful
 */
export async function registerPushToken(sessionToken, userRole, ownerId) {
  try {
    if (!sessionToken || !userRole || !ownerId) {
      console.warn('Missing required parameters for token registration');
      return false;
    }

    const fcmToken = await getFCMToken();
    if (!fcmToken) {
      console.log('Could not obtain FCM token, skipping registration');
      return false;
    }

    const registerUrl = `${API_URL.BASE_NOTTFICATION}/rest/big-local/api/v1/notification/device/register`;

    const headers = {
      'Content-Type': 'application/json',
      sessionToken: sessionToken,
      'X-USER-ROLE': userRole,
    };

    // Add appropriate ID header based on role
    if (userRole === USER_ROLES.VENDOR) {
      headers['X-Vendor-Id'] = ownerId;
    } else {
      headers['X-User-Id'] = ownerId;
    }

    const response = await fetch(registerUrl, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        fcmToken: fcmToken,
        platform: 'WEB', // Platform identifier for web
      }),
    });

    if (response.ok) {
      console.log('✅ FCM token registered successfully with backend');
      // Store token in localStorage for future reference
      localStorage.setItem('fcmToken', fcmToken);
      return true;
    } else {
      const errorText = await response.text();
      console.error('Failed to register FCM token:', response.status, errorText);
      return false;
    }
  } catch (error) {
    console.error('Error registering push token:', error);
    // Don't throw - this is a background operation that shouldn't block login
    return false;
  }
}

/**
 * Unregister FCM token from backend on logout
 * @param {string} sessionToken - User session token
 * @param {string} userRole - User role (Vendor or User)
 * @param {string} ownerId - User ID or Vendor ID
 * @returns {Promise<boolean>} Whether unregistration was successful
 */
export async function unregisterPushToken(sessionToken, userRole, ownerId) {
  try {
    const fcmToken = localStorage.getItem('fcmToken');
    if (!fcmToken) {
      console.log('No FCM token found, skipping unregistration');
      return false;
    }

    if (!sessionToken || !userRole || !ownerId) {
      console.warn('Missing required parameters for token unregistration');
      return false;
    }

    const unregisterUrl = `${API_URL.BASE_NOTTFICATION}/rest/big-local/api/v1/notification/device/unregister`;

    const headers = {
      'Content-Type': 'application/json',
      sessionToken: sessionToken,
      'X-USER-ROLE': userRole,
    };

    // Add appropriate ID header based on role
    if (userRole === USER_ROLES.VENDOR) {
      headers['X-Vendor-Id'] = ownerId;
    } else {
      headers['X-User-Id'] = ownerId;
    }

    const response = await fetch(unregisterUrl, {
      method: 'DELETE',
      headers: headers,
      body: JSON.stringify({
        fcmToken: fcmToken,
      }),
    });

    if (response.ok || response.status === 204) {
      console.log('✅ FCM token unregistered successfully');
      localStorage.removeItem('fcmToken');
      return true;
    } else {
      console.error('Failed to unregister FCM token:', response.status);
      return false;
    }
  } catch (error) {
    console.error('Error unregistering push token:', error);
    // Don't throw - this is a background operation that shouldn't block logout
    return false;
  }
}

/**
 * Setup token refresh listener
 * Automatically re-register token when it changes
 * @param {string} sessionToken - User session token
 * @param {string} userRole - User role (Vendor or User)
 * @param {string} ownerId - User ID or Vendor ID
 */
export function setupTokenRefreshListener(sessionToken, userRole, ownerId) {
  if (!messaging) {
    return;
  }

  // Firebase automatically handles token refresh
  // We just need to re-register when the app starts
  // This is handled in the UserContext
  console.log('Token refresh listener setup complete');
}

/**
 * Check if notifications are supported and enabled
 * @returns {boolean} Whether notifications are available
 */
export function areNotificationsSupported() {
  return 'Notification' in window && 'serviceWorker' in navigator;
}

/**
 * Get current notification permission status
 * @returns {NotificationPermission} Current permission status
 */
export function getNotificationPermission() {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return Notification.permission;
}
